import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { CelularSpecs, ItemType } from "../../types/types";
import { CELULAR_SPEC_LABELS, PRODUCT_DETAIL_TEXTS } from "../../pages/shared/constants";

/**
 * Three columns from `md` (EB-557).
 *
 * Two was right while this lived in the right-hand column of the product grid and had half a page.
 * Full width, two columns put ~470 px between a label and its value on a 1440 px screen, which is
 * a long way for the eye to travel to pair "Batería" with "3969 mAh". A third column spends the
 * width on more rows visible at once instead of on stretching each one.
 */
const SpecsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr",
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
}));

/**
 * The banding follows grid **rows**, which is what the striping was always meant to mean.
 *
 * It used to come from a `$even` prop on the entry's index. A grid fills row-wise, so at two
 * columns every even index landed in the left column and the "zebra" was really a shaded left
 * column — legible enough squeezed into half a page, plainly wrong once the table spans the
 * screen, and wrong in a third way at three columns. `nth-of-type` per breakpoint is the only
 * version that survives the column count changing, since JS never learns what CSS decided.
 */
const bandedRows = (columns: number) =>
  Array.from(
    { length: columns },
    (_, offset) => `&:nth-of-type(${columns * 2}n + ${offset + 1})`,
  ).join(", ");

const SpecRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.spacing(0.5),
  // The three ranges are exclusive rather than `up()` overrides on purpose. An `up()` chain would
  // need each breakpoint to switch the previous one's banding off, and `backgroundColor` on the
  // root loses to a `nth-of-type` selector from a lower breakpoint no matter which came last — so
  // the three sets unioned and all but one row ended up shaded.
  [theme.breakpoints.down("sm")]: {
    [bandedRows(1)]: { backgroundColor: theme.palette.action.hover },
  },
  [theme.breakpoints.between("sm", "md")]: {
    [bandedRows(2)]: { backgroundColor: theme.palette.action.hover },
  },
  [theme.breakpoints.up("md")]: {
    [bandedRows(3)]: { backgroundColor: theme.palette.action.hover },
  },
}));

function getLabel(key: string): string {
  if (key in CELULAR_SPEC_LABELS) {
    return CELULAR_SPEC_LABELS[key as keyof CelularSpecs];
  }
  return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

interface PhoneSpecsProps {
  item: ItemType;
}

const PhoneSpecs: React.FC<PhoneSpecsProps> = ({ item }) => {
  if (!item.specs) return null;

  const entries = Object.entries(item.specs).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );

  if (entries.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        {PRODUCT_DETAIL_TEXTS.specsTitle}
      </Typography>
      <SpecsContainer mt={1}>
        {entries.map(([key, value]) => (
          <SpecRow key={key}>
            <Typography variant="body2" fontWeight="bold">
              {getLabel(key)}
            </Typography>
            <Typography variant="body2">{String(value)}</Typography>
          </SpecRow>
        ))}
      </SpecsContainer>
    </Box>
  );
};

export default PhoneSpecs;
